import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, MapPin, Navigation, RotateCcw, CreditCard, Banknote } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full bg-obsidian border border-amber-500/20 rounded-md px-4 py-3 text-sm text-ivory placeholder:text-zinc-600 focus:outline-none focus:border-amber-400/60 focus:shadow-[0_0_20px_rgba(212,175,55,0.08)] transition-all duration-300 [color-scheme:dark]";
const labelCls = "block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-2";

const BookingForm = () => {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ pickup: "", destination: "", date: "", time: "", passengers: 1, luggage: 0, name: "", phone: "", email: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("deposit");
  const [riveSide, setRiveSide] = useState("rd");

  const fareOf = () => {
    const s = `${form.pickup} ${form.destination}`.toLowerCase();
    if (s.includes("cdg") || s.includes("charles de gaulle") || s.includes("roissy")) {
      return riveSide === "rg" ? { key: "fare_cdg_rg", amount: 65, from: false, airport: true } : { key: "fare_cdg_rd", amount: 56, from: false, airport: true };
    }
    if (s.includes("orly")) {
      return riveSide === "rg" ? { key: "fare_orly_rg", amount: 36, from: false, airport: true } : { key: "fare_orly_rd", amount: 45, from: false, airport: true };
    }
    return { key: "fare_city", amount: 35, from: true, airport: false };
  };

  const depositOf = () => {
    const f = fareOf();
    const dep = Math.round(f.amount * 25) / 100;
    return { key: `${f.key}_dep`, amount: dep, solde: Math.round((f.amount - dep) * 100) / 100, airport: f.airport, from: f.from };
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/bookings`, { ...form, passengers: Number(form.passengers), luggage: Number(form.luggage), lang, payment_method: paymentMethod });
      if (paymentMethod === "online" || paymentMethod === "deposit") {
        toast.info(t.booking?.redirecting);
        const fare = paymentMethod === "deposit" ? depositOf() : fareOf();
        const pay = await axios.post(`${API}/payments/checkout`, { lookup_key: fare.key, origin_url: window.location.origin, booking_reference: res.data.reference });
        window.location.href = pay.data.checkout_url;
        return;
      }
      setReference(res.data.reference);
      toast.success(t.booking?.successTitle, { description: `${t.booking?.successRef} : ${res.data.reference}` });
    } catch (err) {
      toast.error(t.booking?.error);
      setLoading(false);
    }
  };

  const reset = () => {
    setReference(null);
    setForm({ pickup: "", destination: "", date: "", time: "", passengers: 1, luggage: 0, name: "", phone: "", email: "", notes: "" });
  };

  return (
    <section id="booking" data-testid="booking-section" className="relative py-20 lg:py-32 bg-surface/30">
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <Reveal className="text-center">
          <p data-testid="booking-eyebrow" className="text-xs uppercase tracking-[0.3em] font-mono text-amber-700/90 mb-4">{t.booking?.eyebrow}</p>
          <h2 data-testid="booking-title" className="font-serif text-2xl sm:text-3xl lg:text-5xl tracking-tight text-ivory">{t.booking?.title}</h2>
          <p data-testid="booking-sub" className="mt-4 text-sm text-zinc-500 max-w-xl mx-auto">{t.booking?.sub}</p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 bg-surface border border-amber-500/20 hover:border-amber-500/30 rounded-lg p-6 sm:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.5)] transition-colors duration-500">
            <AnimatePresence mode="wait">
              {reference ? (
                <motion.div
                  key="success"
                  data-testid="booking-success-panel"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center py-10"
                >
                  <CheckCircle2 size={48} strokeWidth={1.2} className="text-champagne mx-auto" />
                  <h3 className="font-serif text-2xl sm:text-3xl text-ivory mt-6">{t.booking?.successTitle}</h3>
                  <p className="text-sm text-zinc-600 mt-3">{t.booking?.successText}</p>
                  <div className="mt-8 inline-block border border-amber-500/30 rounded-md px-8 py-4 bg-obsidian">
                    <span className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 block">{t.booking?.successRef}</span>
                    <span data-testid="booking-reference-code" className="font-mono text-2xl text-champagne tracking-widest">{reference}</span>
                  </div>
                  <div className="mt-8">
                    <button data-testid="new-booking-btn" onClick={reset} className="flex items-center gap-2 mx-auto text-xs uppercase tracking-[0.2em] text-zinc-600 hover:text-champagne border border-amber-500/20 hover:border-amber-400/60 rounded-full px-6 py-3 transition-colors duration-300">
                      <RotateCcw size={13} /> {t.booking?.newBooking}
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.form key="form" data-testid="booking-form" onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="grid sm:grid-cols-2 gap-6">
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="pickup">{t.booking?.pickup}</label>
                    <div className="relative">
                      <MapPin size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-champagne/60" />
                      <input id="pickup" data-testid="pickup-input" required minLength={2} value={form.pickup} onChange={set("pickup")} placeholder={t.booking?.placeholders?.pickup} className={`${inputCls} pl-11`} />
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {t.booking?.presets?.map((p) => (
                        <button key={p} type="button" data-testid={`preset-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setForm({ ...form, pickup: p })}
                          className="text-[11px] uppercase tracking-wider text-zinc-600 hover:text-champagne border border-amber-500/15 hover:border-amber-400/50 rounded-full px-3 py-1.5 transition-colors duration-300">
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="destination">{t.booking?.destination}</label>
                    <div className="relative">
                      <Navigation size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-champagne/60" />
                      <input id="destination" data-testid="destination-input" required minLength={2} value={form.destination} onChange={set("destination")} placeholder={t.booking?.placeholders?.destination} className={`${inputCls} pl-11`} />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="date">{t.booking?.date}</label>
                    <input id="date" data-testid="date-input" type="date" required value={form.date} onChange={set("date")} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="time">{t.booking?.time}</label>
                    <input id="time" data-testid="time-input" type="time" required value={form.time} onChange={set("time")} className={inputCls} />
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="passengers">{t.booking?.passengers}</label>
                    <select id="passengers" data-testid="passengers-select" value={form.passengers} onChange={set("passengers")} className={inputCls}>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="luggage">{t.booking?.luggage}</label>
                    <select id="luggage" data-testid="luggage-select" value={form.luggage} onChange={set("luggage")} className={inputCls}>
                      {[0, 1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="name">{t.booking?.name}</label>
                    <input id="name" data-testid="name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder={t.booking?.placeholders?.name} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="phone">{t.booking?.phone}</label>
                    <input id="phone" data-testid="phone-input" type="tel" required minLength={6} value={form.phone} onChange={set("phone")} placeholder={t.booking?.placeholders?.phone} className={inputCls} />
                  </div>

                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="email">{t.booking?.emailLabel}</label>
                    <input id="email" data-testid="email-input" type="email" value={form.email} onChange={set("email")} placeholder="nom@exemple.com" className={inputCls} />
                  </div>

                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="notes">{t.booking?.notes}</label>
                    <textarea id="notes" data-testid="notes-input" rows={3} value={form.notes} onChange={set("notes")} className={`${inputCls} resize-none`} />
                  </div>

                  <div className="sm:col-span-2">
                    <label className={labelCls}>{t.booking?.paymentTitle}</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button type="button" data-testid="pay-deposit-btn" onClick={() => setPaymentMethod("deposit")}
                        className={`flex items-center justify-center gap-2 rounded-md border px-4 py-3.5 text-xs uppercase tracking-[0.15em] font-semibold transition-all duration-300 ${paymentMethod === "deposit" ? "border-amber-600/70 bg-champagne/10 text-champagne shadow-[0_0_24px_rgba(154,123,36,0.15)]" : "border-amber-600/20 text-zinc-500 hover:border-amber-600/50 hover:text-champagne"}`}>
                        <CreditCard size={15} /> {t.booking?.payDeposit}
                      </button>
                      <button type="button" data-testid="pay-online-btn" onClick={() => setPaymentMethod("online")}
                        className={`flex items-center justify-center gap-2 rounded-md border px-4 py-3.5 text-xs uppercase tracking-[0.15em] font-semibold transition-all duration-300 ${paymentMethod === "online" ? "border-amber-600/70 bg-champagne/10 text-champagne shadow-[0_0_24px_rgba(154,123,36,0.15)]" : "border-amber-600/20 text-zinc-500 hover:border-amber-600/50 hover:text-champagne"}`}>
                        <CreditCard size={15} /> {t.booking?.payOnline}
                      </button>
                    </div>
                    {fareOf().airport && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {[["rd", t.booking?.riveDroite], ["rg", t.booking?.riveGauche]].map(([v, label]) => (
                          <button key={v} type="button" data-testid={`rive-${v}-btn`} onClick={() => setRiveSide(v)}
                            className={`text-[11px] uppercase tracking-wider rounded-full px-3 py-1.5 border transition-colors duration-300 ${riveSide === v ? "border-amber-600/70 bg-champagne/10 text-champagne font-semibold" : "border-amber-600/20 text-zinc-500 hover:border-amber-600/50"}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                    {paymentMethod === "deposit" ? (
                      <p data-testid="fare-estimate" className="mt-3 text-xs font-mono text-champagne">
                        {t.booking?.depositLabel} : {fareOf().from ? t.booking?.fareFrom : ""}{depositOf().amount.toLocaleString("fr-FR")} € — {t.booking?.remainderLabel} : {depositOf().solde.toLocaleString("fr-FR")} €
                      </p>
                    ) : (
                      <p data-testid="fare-estimate" className="mt-3 text-xs font-mono text-champagne">
                        {t.booking?.fareEstimate} : {fareOf().from ? t.booking?.fareFrom : ""}{fareOf().amount} €
                      </p>
                    )}
                  </div>

                  <div className="sm:col-span-2 pt-2">
                    <button
                      data-testid="booking-submit-btn"
                      type="submit"
                      disabled={loading}
                      className="w-full bg-champagne text-obsidian text-sm font-semibold uppercase tracking-[0.2em] rounded-full px-8 py-4 hover:bg-champagne-bright hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] disabled:opacity-60 disabled:cursor-wait transition-all duration-300"
                    >
                      {loading ? t.booking?.submitting : t.booking?.submit}
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default BookingForm;
